'use client';

import type { TransitionProps, TransitionStatus } from 'notistack';
import { Transition } from 'notistack';
import * as React from 'react';

import { createTransition, getTransitionProps, reflow, useForkRef } from './shared';

const styles: Partial<Record<TransitionStatus, React.CSSProperties>> = {
    entering: {
        opacity: 1,
        transform: 'translateY(0)',
    },
    entered: {
        opacity: 1,
        transform: 'translateY(0)',
    },
};

export const FadeSlide = React.forwardRef<HTMLDivElement, TransitionProps>((props, ref) => {
    const {
        children,
        in: inProp,
        timeout = 300, // Default timeout
        style,
        onEnter,
        onEntered,
        onExit,
        onExited,
        ...other
    } = props;

    const nodeRef = React.useRef<HTMLDivElement>(null);
    const handleRefIntermediary = useForkRef<HTMLDivElement>(
        (children as React.RefAttributes<HTMLDivElement>)?.ref,
        ref
    );
    const handleRef = useForkRef(nodeRef, handleRefIntermediary);

    const handleEnter: TransitionProps['onEnter'] = (node, isAppearing) => {
        reflow(node);

        const transitionProps = getTransitionProps({
            style,
            timeout,
            mode: 'enter',
        });
        node.style.transition = createTransition(transitionProps, ['opacity', 'transform']);

        if (onEnter) {
            onEnter(node, isAppearing);
        }
    };

    const handleExit: TransitionProps['onExit'] = node => {
        const transitionProps = getTransitionProps({
            style,
            timeout,
            mode: 'exit',
        });
        node.style.transition = createTransition(transitionProps, ['opacity', 'transform']);

        if (onExit) {
            onExit(node);
        }
    };

    return (
        <Transition
            appear
            in={inProp}
            nodeRef={nodeRef as React.RefObject<HTMLDivElement>}
            onEnter={handleEnter}
            onEntered={onEntered}
            onExit={handleExit}
            onExited={onExited}
            timeout={timeout}
            {...other}
        >
            {(status, childProps) =>
                React.cloneElement(children as React.ReactElement, {
                    // @ts-expect-error -- TODO [TIGR-249]
                    style: {
                        opacity: 0,
                        transform: 'translateY(20px)',
                        visibility: status === 'exited' && !inProp ? 'hidden' : undefined,
                        ...styles[status],
                        ...style,
                        // @ts-expect-error -- TODO [TIGR-249]
                        ...(children as React.ReactElement).props.style,
                    },
                    ref: handleRef,
                    ...childProps,
                })
            }
        </Transition>
    );
});

FadeSlide.displayName = 'FadeSlide';
